import {
  ConsciousLevel,
  NEWS2State,
  OxygenScale,
  OxygenTherapy,
  ScoreBand,
  ScoreResult,
} from './types';

export const RESPIRATION_BANDS: ScoreBand[] = [
  { max: 8, score: 3, label: '≤8' },
  { min: 9, max: 11, score: 1, label: '9–11' },
  { min: 12, max: 20, score: 0, label: '12–20' },
  { min: 21, max: 24, score: 2, label: '21–24' },
  { min: 25, score: 3, label: '≥25' },
];

export const SPO2_SCALE_1_BANDS: ScoreBand[] = [
  { max: 91, score: 3, label: '≤91' },
  { min: 92, max: 93, score: 2, label: '92–93' },
  { min: 94, max: 95, score: 1, label: '94–95' },
  { min: 96, score: 0, label: '≥96' },
];

const SPO2_SCALE_2_BASE_BANDS: ScoreBand[] = [
  { max: 83, score: 3, label: '≤83' },
  { min: 84, max: 85, score: 2, label: '84–85' },
  { min: 86, max: 87, score: 1, label: '86–87' },
  { min: 88, max: 92, score: 0, label: '88–92' },
];

const SPO2_SCALE_2_AIR_BANDS: ScoreBand[] = [
  ...SPO2_SCALE_2_BASE_BANDS,
  { min: 93, score: 0, label: '≥93 ore' },
];

const SPO2_SCALE_2_OXYGEN_BANDS: ScoreBand[] = [
  ...SPO2_SCALE_2_BASE_BANDS,
  { min: 93, max: 94, score: 1, label: '93–94' },
  { min: 95, max: 96, score: 2, label: '95–96' },
  { min: 97, score: 3, label: '≥97' },
];

export const SYSTOLIC_BANDS: ScoreBand[] = [
  { max: 90, score: 3, label: '≤90' },
  { min: 91, max: 100, score: 2, label: '91–100' },
  { min: 101, max: 110, score: 1, label: '101–110' },
  { min: 111, max: 219, score: 0, label: '111–219' },
  { min: 220, score: 3, label: '≥220' },
];

export const PULSE_BANDS: ScoreBand[] = [
  { max: 40, score: 3, label: '≤40' },
  { min: 41, max: 50, score: 1, label: '41–50' },
  { min: 51, max: 90, score: 0, label: '51–90' },
  { min: 91, max: 110, score: 1, label: '91–110' },
  { min: 111, max: 130, score: 2, label: '111–130' },
  { min: 131, score: 3, label: '≥131' },
];

export const TEMPERATURE_BANDS: ScoreBand[] = [
  { max: 35, score: 3, label: '≤35,0' },
  { min: 35.1, max: 36, score: 1, label: '35,1–36,0' },
  { min: 36.1, max: 38, score: 0, label: '36,1–38,0' },
  { min: 38.1, max: 39, score: 1, label: '38,1–39,0' },
  { min: 39.1, score: 2, label: '≥39,1' },
];

export const getOxygenSaturationBands = (
  scale: OxygenScale,
  therapy: OxygenTherapy,
): ScoreBand[] => {
  if (scale === OxygenScale.SCALE_1) return SPO2_SCALE_1_BANDS;
  return therapy === 'Oxygen' ? SPO2_SCALE_2_OXYGEN_BANDS : SPO2_SCALE_2_AIR_BANDS;
};

export const getScoreForValue = (value: number, bands: ScoreBand[]): number => {
  const band = bands.find(({ min, max }) => {
    const meetsMinimum = min === undefined || value >= min;
    const meetsMaximum = max === undefined || value <= max;
    return meetsMinimum && meetsMaximum;
  });

  return band?.score ?? 0;
};

export const calculateNEWS2 = (state: NEWS2State): ScoreResult => {
  const parameterScores = [
    getScoreForValue(state.respirationRate, RESPIRATION_BANDS),
    getScoreForValue(
      state.oxygenSaturation,
      getOxygenSaturationBands(state.oxygenScale, state.airOrOxygen),
    ),
    getScoreForValue(state.systolicBP, SYSTOLIC_BANDS),
    getScoreForValue(state.pulseRate, PULSE_BANDS),
    state.consciousness === ConsciousLevel.VPU ? 3 : 0,
    getScoreForValue(state.temperature, TEMPERATURE_BANDS),
  ];

  const oxygenSupplement = state.airOrOxygen === 'Oxygen' ? 2 : 0;
  const score = parameterScores.reduce((sum, points) => sum + points, oxygenSupplement);
  const maxSingleScore = Math.max(...parameterScores);

  if (score >= 7) {
    return {
      total: score,
      tone: 'high',
      riskLevel: 'Aukšta',
      frequencyOfMonitoring: 'Nuolatinis gyvybinių funkcijų stebėjimas',
      clinicalResponse:
        'SKUBI PAGALBA: Nedelsiant informuoti gydytoją (specialistą), skubus įvertinimas, apsvarstyti perkėlimą į ITS.',
    };
  }

  if (score >= 5) {
    return {
      total: score,
      tone: 'medium',
      riskLevel: 'Vidutinė',
      frequencyOfMonitoring: 'Kas 1 valandą (minimaliai)',
      clinicalResponse:
        'SKUBU: Skubus gydytojo įvertinimas, užtikrinti klinikinę priežiūrą aplinkoje su stebėjimo įranga.',
    };
  }

  if (maxSingleScore === 3) {
    return {
      total: score,
      tone: 'low',
      riskLevel: 'Maža',
      frequencyOfMonitoring: 'Kas 1 valandą (minimaliai)',
      clinicalResponse:
        '3 BALAI VIENAME PARAMETRE: Informuoti medicinos komandą, peržiūrėti gydymą ir spręsti dėl priežiūros eskalavimo.',
    };
  }

  if (score >= 1) {
    return {
      total: score,
      tone: 'low',
      riskLevel: 'Maža',
      frequencyOfMonitoring: 'Kas 4–6 valandas (minimaliai)',
      clinicalResponse:
        'BENDRA SUMA 1–4: Slaugytojas turi įvertinti pacientą ir nuspręsti, ar reikia dažnesnio stebėjimo.',
    };
  }

  return {
    total: score,
    tone: 'normal',
    riskLevel: 'Normali',
    frequencyOfMonitoring: 'Kas 12 valandų (minimaliai)',
    clinicalResponse: 'SUMA 0: Tęsti įprastą NEWS stebėjimą.',
  };
};
